import { Option, pipe, ReadonlyArray } from "fp";
import { darken } from "polished";
import React, { Dispatch, SetStateAction, useMemo, useState } from "react";

import { Asset } from "~/Asset";
import * as Color from "~/Color";
import { Company } from "~/Company/index";
import * as Data from "~/Data";
import * as Driver from "~/Driver";
import * as ErrorMessage from "~/ErrorMessage";
import { Human } from "~/Human";
import * as Icon from "~/Icon";
import * as Layout from "~/Layout";
import * as Loading from "~/Loading";
import * as Log from "~/Log";
import * as NotificationBadge from "~/NotificationBadge";
import { Radio } from "~/Radio";
import { Remote } from "~/Remote";
import { Time } from "~/Time";
import { Timeline } from "~/Timeline";
import * as Typography from "~/Typography";
import { mergeAdjacentLikeEventsSpanningMidnight } from "./Data";

type ViewOptions = "Details" | "Overview";

type ViewToggleConsumer = {
  readonly Action: Component;
  readonly route: Log.Route;
};

export const Component = (route: Log.Route) => {
  const [viewName, toggleViewName] = useState<ViewOptions>("Details");
  const { reset: clearBrushCache } = Data.SharedState.use<
    Timeline.Brush.BrushSelection
  >("BrushSelection", [0, 0]);
  const { reset: clearOverviewBrushCache } = Data.SharedState.use<
    Timeline.Brush.BrushSelection
  >("OverviewBrushSelection", [0, 0]);

  return useMemo(
    () => (
      <Layout.Section.Component>
        <Layout.Block.Component bottom>
          {viewName === "Details" ? (
            <View.TimeLine
              route={route}
              Action={() => {
                clearOverviewBrushCache();
                return (
                  <View.Toggle
                    viewName={viewName}
                    onChangeView={toggleViewName}
                  />
                );
              }}
            />
          ) : (
            <View.Chart
              route={route}
              Action={() => {
                clearBrushCache();
                return (
                  <View.Toggle
                    viewName={viewName}
                    onChangeView={toggleViewName}
                  />
                );
              }}
            />
          )}
        </Layout.Block.Component>
      </Layout.Section.Component>
    ),
    [viewName, route]
  );
};

export const MenuTitle = ({
  remote = Data.Remote.success(undefined),
  Icon = () => null,
  children,
}: {
  readonly Icon?: Component;
  readonly remote?: Data.Remote<unknown>;
  readonly children: Component.Children;
}) => (
  <Timeline.Menu.Title
    Icon={() =>
      pipe(
        remote,
        Data.Remote.when({
          // TODO: Add error state
          error: () => <Icon />,
          success: () => <Icon />,
          loading: () => <Loading.Icon.Component />,
        })
      )
    }
  >
    {children}
  </Timeline.Menu.Title>
);

export namespace View {
  export const TimeLine: Component<ViewToggleConsumer> = ({
    Action,
    route,
  }) => {
    const defaultZone = Time.Zone.use();
    const driverZone = Driver.useFromReference({
      ...Option.toUndefined(Driver.useReferenceFromRoute()),
      found: (driver) => driver?.time ?? defaultZone,
      missing: () => defaultZone,
    });

    const companyZone = pipe(
      Company.use(),
      ({ company }) =>
        pipe(
          company,
          Remote.map(({ time }) => ({ time })),
          Remote.getOrElse(() => ({ time: undefined as Time.Zone | undefined }))
        ),
      ({ time }) => (time ? time : defaultZone)
    );

    const statusEvents = DutyStatus.useEvents(route);
    return (
      <Timeline
        Action={Action}
        title={"Details"}
        statusEvents={statusEvents}
        milestones={[
          { type: "Driver", ...driverZone },
          { type: "Company", ...companyZone },
          { type: "Local", ...defaultZone },
        ]}
      >
        {Data.Remote.success({
          ...Log.Data.toDataWindow(route),
          now: Time.DateTime.now(),
        })}
        {Violations.use(route)}
        {DutyStatus.use(route)}
        {Revisions.use(route)}
        {TrackerLogs.use(route)}
        {ApplicationLogs.use(route)}
        {PairingStatus.use(route)}
      </Timeline>
    );
  };

  export const Chart: Component<ViewToggleConsumer> = ({ Action, route }) => (
    <Log.Chart.Component Action={Action} route={route} />
  );

  type ViewToggleProps = {
    readonly onChangeView: Dispatch<SetStateAction<ViewOptions>>;
    readonly viewName: ViewOptions;
  };

  export const Toggle: Component<ViewToggleProps> = ({
    onChangeView,
    viewName,
  }) => (
    <Radio.Group
      onChange={() =>
        viewName === "Details"
          ? onChangeView("Overview")
          : onChangeView("Details")
      }
      value={viewName}
    >
      <Radio.Button value="Details">
        <Layout.Space>
          <Icon.Stream />
          Details
        </Layout.Space>
      </Radio.Button>
      <Radio.Button value="Overview">
        <Layout.Space>
          <Icon.ChartScatter />
          Overview
        </Layout.Space>
      </Radio.Button>
    </Radio.Group>
  );
}

export namespace Violations {
  export const use = (route: Log.Route) =>
    Timeline.Section.use(
      Log.id(route),
      useEvents(route)
    )({
      Menu: () => Menu(route),
      Description,
      href: () => Log.Issue.href(),
      background: () => ErrorMessage.Danger.color(),
    });

  export const useEvents = (route: Log.Route) =>
    pipe(
      Log.Violation.use(route),
      Data.Remote.getOrElse(() => [] as readonly Log.Violation.Violation[])
    );

  const Menu = (route: Log.Route) => {
    const count = Log.Violation.useCountFromActiveLog(route);
    const children = Data.Remote.when(count, {
      loading: () => null,
      error: () => null,
      success: (count) => (
        <Layout.Inline.Component left>
          <NotificationBadge.Component sup danger={count > 0}>
            {count}
          </NotificationBadge.Component>
        </Layout.Inline.Component>
      ),
    });

    return <MenuTitle remote={count}>Violations{children}</MenuTitle>;
  };

  const Description = (violation: Log.Violation.Violation) => (
    <>
      <Timeline.Event.Description.Item>
        <Layout.Inline.Component right>
          <ErrorMessage.Component danger icon>
            <Log.Violation.Container.Type.Component {...violation} />
          </ErrorMessage.Component>
        </Layout.Inline.Component>
      </Timeline.Event.Description.Item>
      <Timeline.Event.Description.Item>
        <Log.Violation.Description.Component {...violation} />
        &nbsp;
      </Timeline.Event.Description.Item>
    </>
  );
}

export namespace DutyStatus {
  export const use = (route: Log.Route) =>
    Timeline.Section.use(
      Log.id(route),
      useMergedEvents(route)
    )({
      Menu: () => Menu(route),
      Description,
      href: () => Log.Event.href(),
      background,
    });

  export const background = ({ status }: Log.Event.Event): Color.Color =>
    Driver.Status.toColor(status);

  export const useEvents = (route: Log.Route) =>
    Log.Data.useWhen(route, {
      unavailable: () => [],
      available: ({ events }) =>
        pipe(
          events,
          ReadonlyArray.map<Log.Event.Event, Log.Event.UniqueEvent>(
            (event) => ({
              id: Log.Event.id(event),
              ...event,
            })
          ),
          Log.Data.constrainEventsToWindow(route)
        ),
    });

  export const useMergedEvents = (route: Log.Route) => {
    const defaultZone = Time.Zone.use();
    const driverZone = Driver.useFromReference({
      ...Option.toUndefined(Driver.useReferenceFromRoute()),
      found: (driver) => driver?.time ?? defaultZone,
      missing: () => defaultZone,
    });
    return pipe(useEvents(route), (events) =>
      mergeAdjacentLikeEventsSpanningMidnight(events, driverZone)
    );
  };

  const Menu = (route: Log.Route) => (
    <MenuTitle
      remote={Log.Data.useWhen(route, {
        unavailable: () => Data.Remote.loading(),
        available: () => Data.Remote.success(undefined),
      })}
      Icon={() => (
        <Timeline.Menu.Explanation>
          The combination of revisions, tracker logs, and application logs.
        </Timeline.Menu.Explanation>
      )}
    >
      Duty Status
    </MenuTitle>
  );

  export const Description = (event: Log.Event.Event) => (
    <>
      <Status {...event} />
      <Distance {...event} />
      <Source {...event} />
      <Location {...event} />
    </>
  );

  export const Status = ({ status }: Log.Event.Event) => (
    <Timeline.Event.Description.Item>
      <Driver.Status.Component status={status} />
      &nbsp;
    </Timeline.Event.Description.Item>
  );

  export const Distance = ({ distance }: Log.Event.Event) =>
    pipe(
      Option.fromNullable(distance),
      Option.chain(Option.fromPredicate((distance) => distance > 0)),
      Option.fold(
        () => null,
        (distance) => (
          <Timeline.Event.Description.Item>
            for&nbsp;
            <Typography.Component strong>{distance} mi</Typography.Component>
            &nbsp;
          </Timeline.Event.Description.Item>
        )
      )
    );

  const Source = ({ sources }: Log.Event.Event) => (
    <Timeline.Event.Description.Item>
      via&nbsp;
      <Typography.Component strong>
        <Log.Event.Source.Component {...sources.current} />
      </Typography.Component>
      &nbsp;
    </Timeline.Event.Description.Item>
  );

  export const Location = ({ location }: Log.Event.Event) =>
    pipe(
      Option.fromNullable(location),
      Option.map((location) => (
        <Timeline.Event.Description.Item>
          at&nbsp;
          <Typography.Component strong>{location}</Typography.Component>
          &nbsp;
        </Timeline.Event.Description.Item>
      )),
      Option.toNullable
    );
}

export namespace Revisions {
  export const use = (route: Log.Route) =>
    Timeline.Section.use(
      Log.id(route),
      useEvents(route)
    )({
      Menu: () => Menu(route),
      Description,
      href: () => Log.Audit.href(),
      background: DutyStatus.background,
      compareFn: Log.Audit.Event.compare,
    });

  export const useEvents = (route: Log.Route) =>
    pipe(
      Log.Audit.use(route),
      Data.Remote.map(({ revisions }) => revisions),
      Data.Remote.getOrElse(() => [] as readonly Log.Audit.Event.Event[])
    );

  const Menu = (route: Log.Route) => (
    <MenuTitle
      remote={Log.Audit.use(route)}
      Icon={() => (
        <Timeline.Menu.Explanation>
          Events manually revised by admins or drivers.
        </Timeline.Menu.Explanation>
      )}
    >
      Revisions
    </MenuTitle>
  );

  const Description = (event: Log.Audit.Event.Event) => (
    <>
      <DutyStatus.Status {...event} />
      <DutyStatus.Distance {...event} />
      <Timeline.Event.Description.Item>
        via&nbsp;
        <Typography.Component strong>Revision</Typography.Component>
        &nbsp;
        <CreatedBy {...event} />
      </Timeline.Event.Description.Item>
      &nbsp;
      <DutyStatus.Location {...event} />
    </>
  );

  const CreatedBy = (event: Log.Audit.Event.Event) =>
    !event.created ? null : (
      <>
        <Timeline.Event.Description.Item>
          at&nbsp;
          <Time.DateTime {...event.created} />
        </Timeline.Event.Description.Item>
        {event.created.user?.name && (
          <Timeline.Event.Description.Item>
            &nbsp;by&nbsp;
            <Typography.Component strong>
              <Human.Name name={event.created.user.name} />
            </Typography.Component>
          </Timeline.Event.Description.Item>
        )}
      </>
    );
}

namespace TrackerLogs {
  export const use = (route: Log.Route) =>
    Timeline.Section.use(
      Log.id(route),
      useEvents(route)
    )({
      ...DutyStatus,
      Menu: () => Menu(route),
      href: () => Log.Audit.href(),
      background: DutyStatus.background,
    });

  const useEvents = (route: Log.Route) =>
    pipe(
      Log.Audit.use(route),
      Data.Remote.map(({ tracker }) => tracker),
      Data.Remote.getOrElse(() => [] as readonly Log.Audit.Event.Event[])
    );

  const Menu = (route: Log.Route) => (
    <MenuTitle
      remote={Log.Audit.use(route)}
      Icon={() => (
        <Timeline.Menu.Explanation>
          Events originating from a vehicle's ELD.
        </Timeline.Menu.Explanation>
      )}
    >
      Tracker Logs
    </MenuTitle>
  );
}

namespace ApplicationLogs {
  export const use = (route: Log.Route) =>
    Timeline.Section.use(
      Log.id(route),
      useEvents(route)
    )({
      ...DutyStatus,
      Menu: () => Menu(route),
      href: () => Log.Audit.href(),
      background: DutyStatus.background,
    });

  const useEvents = (route: Log.Route) =>
    pipe(
      Log.Audit.use(route),
      Data.Remote.map(({ application }) => application),
      Data.Remote.getOrElse(() => [] as readonly Log.Audit.Event.Event[])
    );

  const Menu = (route: Log.Route) => (
    <MenuTitle
      remote={Log.Audit.use(route)}
      Icon={() => (
        <Timeline.Menu.Explanation>
          Events originating from a driver's mobile application.
        </Timeline.Menu.Explanation>
      )}
    >
      Application Logs
    </MenuTitle>
  );
}

export namespace PairingStatus {
  export const use = (route: Log.Route) => {
    const menu = <Menu route={route}/>;
    return Timeline.Section.use(
      Log.id(route),
      useEvents(route)
    )({
      Menu: () => menu,
      Description,
      background: () => darken(0.05)(Color.azure()),
    });
  };

  export const useEvents = (
    route: Log.Route
  ): readonly Driver.Pairing.Pairing[] =>
    Driver.Pairing.useFromDriverAndDateTime(route);

    const Menu = ({route}: {readonly route: Log.Route}) => (
      <MenuTitle remote={Driver.Pairing.useDriverPairings(route)}>
        Pairing Status
      </MenuTitle>
    );

  const Description = (pairing: Driver.Pairing.Pairing) => (
    <Timeline.Event.Description.Item>
      <Icon.Link />
      &nbsp;Paired with&nbsp;
      <Asset.Link {...pairing} />
      &nbsp;
    </Timeline.Event.Description.Item>
  );
}
